import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { NavShell } from "@/components/NavShell";
import { Zap, Copy, Check, Loader2, FileText, Mail, TrendingUp, Sparkles, ShoppingBag, Globe, Download, Type } from "lucide-react";

const TEMPLATES = [
  { id: "blog_post", label: "Blog Post", icon: FileText, desc: "SEO-optimized blog articles" },
  { id: "social_media", label: "Social Media", icon: Sparkles, desc: "Tweets, LinkedIn, Instagram" },
  { id: "marketing_copy", label: "Marketing Copy", icon: TrendingUp, desc: "Ads, landing pages, sales copy" },
  { id: "email", label: "Email", icon: Mail, desc: "Professional & marketing emails" },
  { id: "product_description", label: "Product Description", icon: ShoppingBag, desc: "Compelling product copy" },
  { id: "landing_page", label: "Landing Page", icon: Globe, desc: "High-converting page copy" },
];

const TONES = ["professional", "casual", "friendly", "authoritative", "humorous", "persuasive"];
const LENGTHS = [
  { id: "short", label: "Short", desc: "100-200 words" },
  { id: "medium", label: "Medium", desc: "300-500 words" },
  { id: "long", label: "Long", desc: "700-1000 words" },
];

export default function GeneratePage({ user, apiClient, onLogout, refreshUser }) {
  const [searchParams] = useSearchParams();
  const [template, setTemplate] = useState(searchParams.get("template") || "blog_post");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [extraInstructions, setExtraInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setGenerating(true);
    setError("");
    setResult(null);
    try {
      const res = await apiClient.post("/generate", {
        template,
        topic: topic.trim(),
        tone,
        length,
        extra_instructions: extraInstructions.trim(),
      });
      setResult(res.data);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (result?.content) {
      try {
        await navigator.clipboard.writeText(result.content);
      } catch {
        const textarea = document.createElement('textarea');
        textarea.value = result.content;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadContent = (format) => {
    if (!result?.content) return;
    const filename = `contentforge-${template}-${Date.now()}`;
    let content = result.content;
    let mimeType = 'text/plain';
    let ext = 'txt';
    if (format === 'md') {
      ext = 'md';
      mimeType = 'text/markdown';
    }
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <NavShell user={user} onLogout={onLogout} active="generate">
      <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-8" data-testid="generate-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            <span className="brand-gradient-text">Generate Content</span>
          </h1>
          <p className="text-gray-500">Choose a template, enter your topic, and let AI create.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Controls */}
          <div className="lg:col-span-2 space-y-6">
            {/* Template selection */}
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-3">Template</label>
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTemplate(t.id)}
                    className={`template-card flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all duration-200 ${template === t.id ? 'border-violet-500/50 bg-violet-500/5 shadow-[0_0_15px_rgba(139,92,246,0.1)]' : 'border-white/5 bg-gray-900/50 hover:border-white/10'}`}
                    data-testid={`template-${t.id}`}
                  >
                    <t.icon className={`w-4 h-4 mt-0.5 shrink-0 ${template === t.id ? 'text-violet-400' : 'text-gray-500'}`} />
                    <div>
                      <div className="text-xs font-medium">{t.label}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{t.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Topic */}
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Topic / Subject</label>
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. How to grow a SaaS business from $0 to $10k MRR"
                rows={3}
                className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-500 outline-none resize-none transition-colors duration-200"
                data-testid="topic-input"
              />
            </div>

            {/* Tone */}
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Tone</label>
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTone(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors duration-200 ${tone === t ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' : 'bg-gray-900/50 text-gray-400 border border-white/5 hover:border-white/10'}`}
                    data-testid={`tone-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Length */}
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Length</label>
              <div className="grid grid-cols-3 gap-2">
                {LENGTHS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setLength(l.id)}
                    className={`p-2.5 rounded-xl border text-center transition-colors duration-200 ${length === l.id ? 'border-violet-500/50 bg-violet-500/5' : 'border-white/5 bg-gray-900/50 hover:border-white/10'}`}
                    data-testid={`length-${l.id}`}
                  >
                    <div className="text-xs font-medium">{l.label}</div>
                    <div className="text-[10px] text-gray-500">{l.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Extra instructions */}
            <div>
              <label className="block text-xs font-semibold tracking-wide text-gray-400 uppercase mb-2">Extra Instructions <span className="normal-case text-gray-600">(optional)</span></label>
              <textarea
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                placeholder="Any specific requirements or context..."
                rows={2}
                className="w-full bg-black/20 border border-white/10 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-500 outline-none resize-none transition-colors duration-200"
                data-testid="extra-instructions-input"
              />
            </div>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={generating || !topic.trim() || user?.credits <= 0}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3.5 rounded-xl text-sm font-medium tracking-wide uppercase transition-all duration-200 shadow-[0_0_25px_rgba(139,92,246,0.3)] hover:shadow-[0_0_35px_rgba(139,92,246,0.5)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Generate (1 credit)
                </>
              )}
            </button>
            {user?.credits <= 0 && (
              <p className="text-xs text-red-400 text-center" data-testid="no-credits-msg">No credits remaining. Purchase more to continue.</p>
            )}
          </div>

          {/* Right: Output */}
          <div className="lg:col-span-3">
            <div className="sticky top-6">
              <div className="bg-gray-900/50 border border-white/5 rounded-xl overflow-hidden min-h-[500px] flex flex-col" data-testid="output-container">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                  <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">Output</span>
                  {result && (
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500"><Type className="w-3 h-3" /> {result.word_count || result.content?.split(/\s+/).length || 0} words</span>
                      <span className="text-xs text-gray-600">|</span>
                      <span className="text-xs text-gray-500">{result.credits_remaining} credits left</span>
                      <span className="text-xs text-gray-600">|</span>
                      <button onClick={copyToClipboard} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-violet-400 transition-colors duration-200" data-testid="copy-btn">
                        {copied ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                      </button>
                      <button onClick={() => downloadContent('txt')} className="flex items-center gap-1 text-xs text-gray-400 hover:text-cyan-400 transition-colors duration-200" data-testid="download-txt-btn">
                        <Download className="w-3.5 h-3.5" /> TXT
                      </button>
                      <button onClick={() => downloadContent('md')} className="flex items-center gap-1 text-xs text-gray-400 hover:text-cyan-400 transition-colors duration-200" data-testid="download-md-btn">
                        <Download className="w-3.5 h-3.5" /> MD
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1 p-5 overflow-auto">
                  {error && (
                    <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4" data-testid="generate-error">
                      {error}
                    </div>
                  )}
                  {generating && (
                    <div className="flex flex-col items-center justify-center h-full gap-4" data-testid="generating-state">
                      <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center animate-pulse-glow">
                        <Zap className="w-5 h-5 text-violet-400" />
                      </div>
                      <div className="text-sm text-gray-400">Generating your content...</div>
                      <div className="w-48 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-600 to-indigo-500 rounded-full animate-shimmer" style={{ width: '60%' }} />
                      </div>
                    </div>
                  )}
                  {!generating && result && (
                    <div className="prose prose-invert prose-sm max-w-none animate-fade-in-up" data-testid="generated-content">
                      <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-200">
                        {result.content}
                      </div>
                    </div>
                  )}
                  {!generating && !result && !error && (
                    <div className="flex flex-col items-center justify-center h-full text-center" data-testid="output-placeholder">
                      <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                        <FileText className="w-6 h-6 text-gray-600" />
                      </div>
                      <p className="text-sm text-gray-500 max-w-xs">
                        Your generated content will appear here. Select a template and enter a topic to get started.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NavShell>
  );
}
